import axios from 'axios';

/**
 * Collapses duplicate in-flight reads into a single HTTP request.
 *
 * Opening one course fired storefront_initial_data three times,
 * influencer_content_data_tab_change_v8 twice and load_course_chapters three
 * times — byte-identical responses every time. The callers are siblings spread
 * across Influencer.js, InfluencerTabsBar.js, InfluencerContent.js,
 * SinglePageCollection.js, Community.js and Library.js and cannot see each
 * other, so no per-component guard fixes it, and there are ~50 call sites so
 * converting them individually is a large and risky diff.
 *
 * Installing here instead means every existing and future call site benefits
 * with no edit at all.
 *
 * SAFETY — three deliberate constraints:
 *
 *  1. ALLOWLIST ONLY. These endpoints are POSTs but semantically reads. Nothing
 *     that writes is ever deduped: collapsing two writes would silently drop
 *     one. Any endpoint not listed here behaves exactly as before.
 *
 *  2. KEYED ON METHOD + URL + PAYLOAD. Two callers share a response only when
 *     they asked precisely the same question. A different tab, course or
 *     influencer is a different key and gets its own request.
 *
 *  3. EACH CONSUMER GETS ITS OWN COPY of `data`. This matters: callers here
 *     mutate what they receive — Influencer.js does
 *     `new_influencer_content_list[index]['current_tier'] = -1` on a shallow
 *     copy of res.data.content_list, which writes straight through to the
 *     shared objects. Handing the same response to two consumers without
 *     cloning would let the first corrupt the second's data.
 */

const DEDUPE_ENDPOINTS = [
  'api/influencer/storefront_initial_data/',
  'api/influencer_content_data_tab_change_v8',
  'api/influencer/load_course_chapters',
  'api/influencer/course/library',
  'api/influencer/community/load_progress_stats',
];

const TTL_MS = 2500;

// Entries are only TTL-checked when their own key is read again, so a key that
// is never re-requested is never looked at and never removed. Opening many
// courses in one session would otherwise grow this Map for the lifetime of the
// tab, holding whole API responses alive. Nothing here is worth keeping past its
// TTL, so a write prunes the expired entries first and only falls back to
// evicting the oldest if everything present is still live.
const MAX_CACHE_ENTRIES = 50;

const inflight = new Map();
const cache = new Map();

function pruneCache() {
  const now = Date.now();
  for (const [k, v] of cache) if (now - v.at >= TTL_MS) cache.delete(k);
  // Map iterates in insertion order, so the first keys are the oldest.
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldest = cache.keys().next().value;
    if (oldest === undefined) break;
    cache.delete(oldest);
  }
}

function isDedupable(config) {
  if (config?.__noDedupe) return false;
  const url = config?.url || '';
  return DEDUPE_ENDPOINTS.some(e => url.includes(e));
}

function serialize(data) {
  if (!data) return '';
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const parts = [];
    data.forEach((v, k) => parts.push(`${k}=${typeof v === 'string' ? v : '[blob]'}`));
    // Sorted so field insertion order cannot produce two keys for one question.
    return parts.sort().join('&');
  }
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch (e) {
    return `__nokey__${Math.random()}`;
  }
}

function clone(value) {
  if (value === null || typeof value !== 'object') return value;
  try {
    if (typeof structuredClone === 'function') return structuredClone(value);
  } catch (e) { /* fall through - structuredClone rejects non-cloneables */ }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return value;
  }
}

// A private copy of the response, so one consumer's mutations cannot reach another.
function copyResponse(res) {
  return { ...res, data: clone(res.data) };
}

export function invalidateRequestDedupe(urlFragment) {
  if (!urlFragment) {
    cache.clear();
    inflight.clear();
    return;
  }
  for (const k of [...cache.keys()]) if (k.includes(urlFragment)) cache.delete(k);
  for (const k of [...inflight.keys()]) if (k.includes(urlFragment)) inflight.delete(k);
}

let installed = false;

export function installRequestDedupe() {
  if (installed) return;
  installed = true;

  const base = axios.defaults.adapter;
  if (typeof base !== 'function') return;

  axios.defaults.adapter = function dedupeAdapter(config) {
    if (!isDedupable(config)) {
      // Any request that is NOT an allowlisted read drops the read cache.
      //
      // This is what makes the TTL safe to have at all. The five allowlisted
      // endpoints are reached from ~84 call sites, and only the handful using
      // loadCourseChapters()/dedupedPost() can pass `force` — the rest call
      // axios directly and have no idea a cache exists. Without this, any one
      // of them that saved something and re-read within TTL_MS would render
      // pre-save data, and the call site would give no hint why.
      //
      // Everything in this codebase POSTs, reads included, so the method alone
      // cannot identify a write — but by the allowlist's own reasoning, a POST
      // that is not an allowlisted read is a write. Over-clearing is harmless
      // (it costs a re-fetch); under-clearing shows stale data.
      const method = (config?.method || 'get').toLowerCase();
      if (method !== 'get' && cache.size) cache.clear();
      return base(config);
    }

    // X-Acting-As is part of the key, not just the method/url/payload.
    //
    // App.js installs a request interceptor that stamps this header from
    // localStorage when an admin is managing someone else's account, and
    // interceptors run before the adapter — so two requests can be identical in
    // method, url and body and still be asking about DIFFERENT creators. Keying
    // without it would let one creator's response be handed to a request made on
    // behalf of another.
    //
    // Not reachable today: every place that sets or clears acting_as_creator
    // (AdminAccounts.js, Settings.js, CreatorProfile.js, DesktopHeaderBreadcrumbs.jsx)
    // follows it with window.location.assign(), and a full navigation discards
    // this Map along with the rest of the JS context. It is in the key so that
    // stops being load-bearing — a future in-place account switch would
    // otherwise leak data across accounts silently.
    const actingAs = config.headers?.['X-Acting-As'] || '';
    const key = `${(config.method || 'get').toLowerCase()}|${config.url}|${actingAs}|${serialize(config.data)}`;

    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) {
      return Promise.resolve(copyResponse(hit.res));
    }

    const pending = inflight.get(key);
    if (pending) {
      return pending.then(copyResponse);
    }

    const req = base(config)
      .then(res => {
        // Cleared FIRST. If anything below threw, the key would otherwise stay
        // in `inflight` pointing at an already-resolved promise, and every
        // later request for it would be handed that one response for the rest
        // of the session — a permanent stale serve rather than a one-off.
        inflight.delete(key);
        cache.set(key, { at: Date.now(), res });
        pruneCache();
        return res;
      })
      .catch(err => {
        // Never cache a failure - the next caller should get a real attempt.
        inflight.delete(key);
        throw err;
      });

    inflight.set(key, req);
    return req.then(copyResponse);
  };
}
